import { Button, Typography } from "@mui/material";
import { useEventCallback } from "@mui/material/utils";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type {
  HumanInTheLoopDecision,
  HumanInTheLoopRequest,
  HumanInTheLoopResponse,
} from "./human-in-the-loop";
import HumanInTheLoopReview from "./HumanInTheLoopReview";
import {
  PendingToolReviewScope,
  PendingToolReviewsProvider,
} from "./PendingToolReviewScope";

export type PendingHumanInTheLoopInterrupt = {
  interruptId: string;
  namespace: string[];
  request: HumanInTheLoopRequest;
  agentName?: string;
  subagentId?: string;
};

// Include the proposal so that changed arguments always require a new review.
const interruptKey = (interrupt: PendingHumanInTheLoopInterrupt) =>
  JSON.stringify([interrupt.interruptId, interrupt.request]);

export const PendingHumanInTheLoopReviews = ({
  interrupts,
  isLoading,
  onRespond,
  approvalContainer,
  visible = true,
  children,
}: {
  interrupts: PendingHumanInTheLoopInterrupt[];
  isLoading: boolean;
  onRespond: (responses: HumanInTheLoopResponse[]) => Promise<void>;
  approvalContainer: HTMLDivElement | null;
  visible?: boolean;
  children?: ReactNode;
}) => {
  const entries = interrupts.map((interrupt) => ({
    key: interruptKey(interrupt),
    interrupt,
  }));
  const keys = entries.map(({ key }) => key);
  const identity = JSON.stringify([...keys].sort());
  const [order, setOrder] = useState(keys);
  const orderedKeys = [
    ...order.filter((key) => keys.includes(key)),
    ...keys.filter((key) => !order.includes(key)),
  ];
  useEffect(() => {
    setOrder(orderedKeys);
  }, [identity]);
  const ordered = orderedKeys.flatMap((key) =>
    entries.filter((entry) => entry.key === key)
  );
  const items = ordered.flatMap(({ key, interrupt }) =>
    interrupt.request.action_requests.map((_, index) => `${key}:${index}`)
  );
  const [decisions, setDecisions] = useState<
    Record<string, HumanInTheLoopDecision>
  >({});
  const [selectedId, setSelectedId] = useState<string>();
  const [showAll, setShowAll] = useState(false);
  const [controlsContainer, setControlsContainer] =
    useState<HTMLDivElement | null>(null);
  const pending = items.filter((id) => !decisions[id]);
  const activeId =
    selectedId && pending.includes(selectedId) ? selectedId : pending[0];
  const submittingRef = useRef(false);
  const lastDecisionId = useRef<string>();
  const [submission, setSubmission] = useState<{
    identity: string;
    phase: "submitting" | "submitted";
  }>();
  const ready = items.length > 0 && pending.length === 0;
  const submitting =
    ready &&
    !(submission?.identity === identity && submission.phase === "submitted");
  const submitted =
    submission?.identity === identity && submission.phase === "submitted";
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setDecisions((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([id]) => items.includes(id))
      )
    );
  }, [identity]);

  const setDecision = useEventCallback(
    (key: string, index: number, decision: HumanInTheLoopDecision) => {
      const id = `${key}:${index}`;
      if (
        submittingRef.current ||
        submitted ||
        !items.includes(id) ||
        decisions[id]
      )
        return;
      lastDecisionId.current = id;
      setDecisions((current) => ({ ...current, [id]: decision }));
      setShowAll(false);
    }
  );

  const submitDecisions = useEventCallback(async () => {
    if (!ready || submittingRef.current || submitted) return;
    const id = lastDecisionId.current!;
    // Build the response from the same decisions that drive the cards and forms.
    const responses = entries.map(({ key, interrupt }) => ({
      decisions: interrupt.request.action_requests.map(
        (_, index) => decisions[`${key}:${index}`]
      ),
    }));
    submittingRef.current = true;
    setSubmission({ identity, phase: "submitting" });
    setError(null);
    try {
      await onRespond(responses);
      setSubmission({ identity, phase: "submitted" });
    } catch {
      setError(
        "Could not submit approvals. Review the selected request and try again."
      );
      setDecisions((current) => {
        const { [id]: failed, ...remaining } = current;
        return remaining;
      });
      setSubmission(undefined);
    } finally {
      submittingRef.current = false;
    }
  });

  useEffect(() => {
    void submitDecisions();
  }, [decisions, identity, submission, submitDecisions]);

  const reviews = ordered.flatMap(({ key, interrupt }) =>
    interrupt.request.action_requests.map((action, actionIndex) => ({
      key: `${key}:${actionIndex}`,
      subagentId: interrupt.subagentId,
      namespace: interrupt.namespace,
      actions: [action],
      action,
      decision: decisions[`${key}:${actionIndex}`]?.type,
      agentName:
        interrupt.agentName ??
        (interrupt.namespace.length ? "Subagent" : "Main agent"),
      review: (
        <div key={`${key}:${actionIndex}`} hidden={!visible}>
          <HumanInTheLoopReview
            value={{
              request: interrupt.request,
              decisions: interrupt.request.action_requests.map(
                (_, index) => decisions[`${key}:${index}`]
              ),
              setDecision: (index, decision) =>
                setDecision(key, index, decision),
              isLoading: isLoading || submitting || submitted,
            }}
            approvals={interrupt.request.action_requests.map((_, index) => {
              const id = `${key}:${index}`;
              return {
                container: controlsContainer,
                selected: id === activeId,
                showAll,
                decision: decisions[id],
                agentName:
                  interrupt.agentName ??
                  (interrupt.namespace.length ? "Subagent" : "Main agent"),
                select: () => {
                  setSelectedId(id);
                  setShowAll(false);
                },
              };
            })}
            actionIndex={actionIndex}
          />
        </div>
      ),
    }))
  );

  return (
    <PendingToolReviewsProvider value={reviews}>
      <PendingToolReviewScope>{children}</PendingToolReviewScope>
      {interrupts.length > 0 &&
        approvalContainer &&
        createPortal(
          <section
            aria-label="Approval queue"
            hidden={!visible}
            className="w-full rounded-md border border-solid border-gray-200 bg-gray-50 p-3 mb-2"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <Typography
                component="span"
                className="text-sm font-medium"
                color={
                  !submitting && !submitted ? "warning.main" : "text.primary"
                }
                role="status"
              >
                {submitting
                  ? "Submitting decisions…"
                  : submitted
                    ? "Decisions submitted"
                    : `${pending.length} ${pending.length === 1 ? "approval" : "approvals"} needed`}
              </Typography>
              {pending.length > 1 && (
                <Button
                  size="small"
                  color="inherit"
                  aria-expanded={showAll}
                  onClick={() => setShowAll((current) => !current)}
                >
                  {showAll ? "Back" : "View all"}
                </Button>
              )}
            </div>
            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <div
              ref={setControlsContainer}
              className={showAll ? "max-h-48 overflow-y-auto" : undefined}
            />
          </section>,
          approvalContainer
        )}
    </PendingToolReviewsProvider>
  );
};
